'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import {
  useLabelPrintConfig,
  useGridPrintConfig,
  useContinuousPrintConfig,
  useActiveZebraPrinterUid,
  useZebraPrinterProfiles
} from '@/features/configurations';
import { cssNumber } from '@/lib/css-value';
import {
  buildBatchLabels,
  buildGridZpl,
  matchProfile,
  previewLabelsAll,
  printGridLabels,
  printLabels,
  profileToOptions,
  type PreviewItem,
  type QrLabelInput
} from '@/lib/zebra';
import {
  QrCode,
  Pencil,
  Printer,
  Check,
  Minus,
  Plus,
  ArrowRight,
  Eye,
  Loader2,
  Copy,
  ExternalLink
} from 'lucide-react';

const ZEBRA_MOCK_ENABLED = process.env.NEXT_PUBLIC_ZEBRA_MOCK === '1';
// Labelary makes outbound HTTP calls to api.labelary.com — keep it dev-only.
const LABELARY_PREVIEW_ENABLED = process.env.NODE_ENV === 'development';

import { PrintPortal } from '@/components/print-portal';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import type { Registration } from '@/features/registration';
import { useAdvanceStageMutation } from '@/features/registration';
import { useUpdatePrintBatchMutation } from '../services/sticking.mutations';

interface QrDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registration: Registration;
}

export function QrDataDialog({
  open,
  onOpenChange,
  registration
}: QrDataDialogProps) {
  const t = useTranslations('sticking');
  const {
    labelWidthMm,
    labelHeightMm,
    gapMm,
    labelDpi,
    qrHeaderGapMm,
    qrFooterGapMm,
    qrHeaderFontMm,
    qrFooterFontMm
  } = useLabelPrintConfig();
  const gridConfig = useGridPrintConfig();
  const continuousConfig = useContinuousPrintConfig();
  const printerUid = useActiveZebraPrinterUid();
  const profiles = useZebraPrinterProfiles();

  // Geometry derived from label_*_mm + continuous_columns. Each ZPL feed
  // paints `cols` labels in one row of the roll; the printer's gap sensor
  // advances exactly one label-pitch (label_height + gap_mm) per feed.
  const continuousLayout = useMemo(() => {
    const cols = Math.max(1, continuousConfig.columns);
    const paperWidthMm =
      cols * labelWidthMm +
      (cols - 1) * gapMm +
      2 * continuousConfig.edgeMarginMm;
    const paperHeightMm = labelHeightMm + gapMm;
    return {
      paperWidthMm,
      paperHeightMm,
      cols,
      rows: 1,
      marginMm: continuousConfig.edgeMarginMm,
      gapMm,
      dpi: labelDpi,
      qrHeaderGapMm,
      qrFooterGapMm,
      qrHeaderFontMm,
      qrFooterFontMm
    };
  }, [
    continuousConfig.columns,
    continuousConfig.edgeMarginMm,
    labelWidthMm,
    labelHeightMm,
    gapMm,
    labelDpi,
    qrHeaderGapMm,
    qrFooterGapMm,
    qrHeaderFontMm,
    qrFooterFontMm
  ]);

  // Guarded copies for the print <style> blocks below, which interpolate these
  // straight into CSS. They are operator-editable settings that arrive as
  // untyped JSON, so a string value would otherwise be written verbatim into
  // the stylesheet — `}</style>…` and all (CWE-79). The ZPL path keeps the raw
  // values; only the innerHTML sink is guarded.
  const styleHeaderGapMm = cssNumber(qrHeaderGapMm, 0);
  const styleFooterGapMm = cssNumber(qrFooterGapMm, 0);
  const styleHeaderFontMm = cssNumber(qrHeaderFontMm, 0);
  const styleFooterFontMm = cssNumber(qrFooterFontMm, 0);

  const [editing, setEditing] = useState(false);
  const [editStartPage, setEditStartPage] = useState(1);
  const [editEndPage, setEditEndPage] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewItems, setPreviewItems] = useState<PreviewItem[] | null>(null);
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [printScope, setPrintScope] = useState<'all' | 'extra'>('all');

  const updateMutation = useUpdatePrintBatchMutation(registration.id);
  const advanceStageMutation = useAdvanceStageMutation();

  // Track the latest preview items in a ref so the unmount cleanup can
  // revoke whatever's currently rendered (the empty-deps cleanup captures
  // the FIRST render's value otherwise — which is always null).
  const previewItemsRef = useRef<PreviewItem[] | null>(null);
  useEffect(() => {
    previewItemsRef.current = previewItems;
  }, [previewItems]);

  // Revoke any leftover blob URLs on unmount so we don't leak when the user
  // navigates away mid-preview. Explicit dismissal paths also revoke via
  // clearPreviewItems().
  useEffect(() => {
    return () => {
      previewItemsRef.current?.forEach((p) => p.revoke());
    };
  }, []);

  function clearPreviewItems() {
    if (!previewItems) return;
    previewItems.forEach((p) => p.revoke());
    setPreviewItems(null);
  }

  const hasBatch = !!registration.latest_batch_id;
  const startPage = registration.latest_batch_start_page ?? 0;
  const endPage = registration.latest_batch_end_page ?? 0;
  const totalSheets = hasBatch ? endPage - startPage + 1 : 0;

  const entityName = useMemo(() => {
    if (registration.form_responses) {
      const responses = registration.form_responses;
      const firstKey = Object.keys(responses)[0];
      if (firstKey) {
        const val = responses[firstKey];
        if (typeof val === 'string' && val.trim()) return val;
      }
    }
    return registration.entity_name_ar || '';
  }, [registration]);

  function handleStartEdit() {
    setEditStartPage(startPage);
    setEditEndPage(endPage);
    setEditing(true);
  }

  function handleCancelEdit() {
    setEditing(false);
  }

  const editTotalSheets = editing ? editEndPage - editStartPage + 1 : 0;
  const sheetDiff = editing ? editTotalSheets - totalSheets : 0;

  async function handleSaveEdit() {
    if (!registration.latest_batch_id) return;
    try {
      await updateMutation.mutateAsync({
        batchId: registration.latest_batch_id,
        data: { start_page: editStartPage, end_page: editEndPage }
      });
      setEditing(false);
    } catch {
      // handled by global MutationCache
    }
  }

  async function handleCompleteIndexing() {
    try {
      await advanceStageMutation.mutateAsync({
        id: registration.id,
        data: { from: 'sticking' }
      });
      onOpenChange(false);
    } catch {
      // handled by global MutationCache
    }
  }

  const printPages = useMemo(() => {
    if (!hasBatch) return [];
    const s = editing ? editStartPage : startPage;
    const e = editing ? editEndPage : endPage;
    const pages = [];
    for (let i = s; i <= e; i++) {
      pages.push({
        page: i,
        containerNumber: registration.container_number
      });
    }
    return pages;
  }, [
    hasBatch,
    editing,
    editStartPage,
    editEndPage,
    startPage,
    endPage,
    registration.container_number
  ]);

  // Pages added by editing the batch (current range minus original range).
  // Used to re-print only the labels the operator hasn't physically printed
  // yet, instead of reprinting the whole range.
  const extraPages = useMemo(() => {
    if (!hasBatch) return [] as typeof printPages;
    const origStart = registration.latest_batch_original_start_page;
    const origEnd = registration.latest_batch_original_end_page;
    if (origStart == null || origEnd == null) return [] as typeof printPages;
    const s = editing ? editStartPage : startPage;
    const e = editing ? editEndPage : endPage;
    const out: typeof printPages = [];
    for (let i = s; i <= e; i++) {
      if (i < origStart || i > origEnd) {
        out.push({ page: i, containerNumber: registration.container_number });
      }
    }
    return out;
  }, [
    hasBatch,
    editing,
    editStartPage,
    editEndPage,
    startPage,
    endPage,
    registration.latest_batch_original_start_page,
    registration.latest_batch_original_end_page,
    registration.container_number
  ]);

  const activePages = printScope === 'extra' ? extraPages : printPages;

  // Sheet metrics — in sticker mode each label is its own sheet; in grid
  // mode `cols × rows` labels share one sheet (one Zebra feed); in
  // continuous mode `columns` labels share one feed (rows=1).
  const cellsPerSheet = useMemo(() => {
    if (gridConfig.mode === 'grid') {
      return Math.max(1, gridConfig.cols * gridConfig.rows);
    }
    if (gridConfig.mode === 'continuous') {
      return Math.max(1, continuousConfig.columns);
    }
    return 1;
  }, [
    gridConfig.mode,
    gridConfig.cols,
    gridConfig.rows,
    continuousConfig.columns
  ]);
  const isDoubleSided = registration.dynamic_form?.is_double_sided === true;

  // Expand each page into one or two label inputs depending on the
  // dynamic form's double-sided flag. Every downstream consumer (Zebra
  // ZPL, in-dialog preview, PrintPortal fallback, Labelary preview)
  // reads from this list so the on-screen counts match the print run.
  const labelInputs = useMemo<QrLabelInput[]>(() => {
    return activePages.flatMap((p) => {
      const base = {
        headerText: p.containerNumber,
        qrPayload: `${p.containerNumber}-P${p.page}`,
        footerText: `P-${p.page}`
      };
      if (!isDoubleSided) return [base];
      return [
        { ...base, footerText: `P-${p.page} · ${t('sideFront')}` },
        { ...base, footerText: `P-${p.page} · ${t('sideBack')}` }
      ];
    });
  }, [activePages, isDoubleSided, t]);

  const sheetCount = useMemo(
    () => Math.max(0, Math.ceil(labelInputs.length / cellsPerSheet)),
    [labelInputs.length, cellsPerSheet]
  );

  // Group label inputs into sheets for the grid/continuous preview.
  const sheets = useMemo(() => {
    if (gridConfig.mode === 'sticker') return [];
    const out: QrLabelInput[][] = [];
    for (let i = 0; i < labelInputs.length; i += cellsPerSheet) {
      out.push(labelInputs.slice(i, i + cellsPerSheet));
    }
    return out;
  }, [gridConfig.mode, labelInputs, cellsPerSheet]);

  async function handlePrint() {
    if (!printerUid) {
      toast.message(t('setDefaultPrinter'));
      window.print();
      return;
    }
    setPrinting(true);

    if (gridConfig.mode === 'grid' || gridConfig.mode === 'continuous') {
      const layout =
        gridConfig.mode === 'continuous'
          ? continuousLayout
          : {
              paperWidthMm: gridConfig.paperWidthMm,
              paperHeightMm: gridConfig.paperHeightMm,
              cols: gridConfig.cols,
              rows: gridConfig.rows,
              marginMm: gridConfig.marginMm,
              gapMm,
              dpi: labelDpi,
              qrHeaderGapMm,
              qrFooterGapMm,
              qrHeaderFontMm,
              qrFooterFontMm
            };
      const result = await printGridLabels({
        printerUid,
        labels: labelInputs,
        layout,
        profiles
      });
      setPrinting(false);
      if (result.ok) {
        toast.success(
          t('printGridSentToZebra', {
            labels: labelInputs.length,
            sheets: result.sheets
          })
        );
      } else {
        toast.error(t('printFailedZebra', { reason: result.error }));
      }
      return;
    }

    const result = await printLabels({
      printerUid,
      labels: labelInputs,
      profiles,
      fallbackOptions: {
        widthMm: labelWidthMm,
        heightMm: labelHeightMm,
        dpi: labelDpi,
        qrHeaderGapMm,
        qrFooterGapMm,
        qrHeaderFontMm,
        qrFooterFontMm
      }
    });
    setPrinting(false);
    if (result.ok) {
      toast.success(t('printSentToZebra', { count: labelInputs.length }));
    } else {
      toast.error(t('printFailedZebra', { reason: result.error }));
    }
  }

  async function handleCopyZpl() {
    if (labelInputs.length === 0) return;
    const inputs = labelInputs;

    let zpl: string;
    if (gridConfig.mode === 'grid') {
      zpl = buildGridZpl(inputs, {
        paperWidthMm: gridConfig.paperWidthMm,
        paperHeightMm: gridConfig.paperHeightMm,
        cols: gridConfig.cols,
        rows: gridConfig.rows,
        marginMm: gridConfig.marginMm,
        gapMm,
        dpi: labelDpi,
        qrHeaderGapMm,
        qrFooterGapMm,
        qrHeaderFontMm,
        qrFooterFontMm
      });
    } else if (gridConfig.mode === 'continuous') {
      zpl = buildGridZpl(inputs, continuousLayout);
    } else {
      const profile = matchProfile(undefined, profiles);
      // The QR↔text gaps are a global UX preference, not a per-printer-
      // profile dimension — always override whatever profileToOptions
      // returned.
      const options = {
        ...(profile
          ? profileToOptions(profile)
          : { widthMm: labelWidthMm, heightMm: labelHeightMm, dpi: labelDpi }),
        qrHeaderGapMm,
        qrFooterGapMm,
        qrHeaderFontMm,
        qrFooterFontMm
      };
      zpl = buildBatchLabels(inputs, options);
    }

    try {
      await navigator.clipboard.writeText(zpl);
      toast.success(
        `ZPL copied (${labelInputs.length} labels${gridConfig.mode === 'grid' ? `, ${sheetCount} sheet(s)` : ''}). Paste into labelary.com/viewer.html.`
      );
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }

  async function handlePreview() {
    if (labelInputs.length === 0) return;
    setPreviewing(true);
    if (previewItems) {
      previewItems.forEach((p) => p.revoke());
      setPreviewItems(null);
    }
    const result = await previewLabelsAll({
      labels: labelInputs,
      profiles,
      fallbackOptions: {
        widthMm: labelWidthMm,
        heightMm: labelHeightMm,
        dpi: labelDpi,
        qrHeaderGapMm,
        qrFooterGapMm,
        qrHeaderFontMm,
        qrFooterFontMm
      }
    });
    setPreviewing(false);
    if (result.ok) {
      setPreviewItems(result.items);
    } else {
      toast.error(`Labelary preview failed: ${result.error}`);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(val) => {
          if (!val) {
            setEditing(false);
            clearPreviewItems();
          }
          onOpenChange(val);
        }}
      >
        <DialogContent className='sm:max-w-3xl'>
          <DialogHeader>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <QrCode className='text-primary h-5 w-5' />
                <DialogTitle className='text-base'>
                  {t('qrDataTitle')}
                </DialogTitle>
                <Badge variant='outline' className='text-xs'>
                  {registration.container_number}
                </Badge>
              </div>
              <div className='flex items-center gap-1'>
                {hasBatch &&
                  !editing &&
                  registration.current_stage === 'sticking' && (
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleStartEdit}
                      title={t('editQrData')}
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                  )}
              </div>
            </div>
          </DialogHeader>

          {!hasBatch ? (
            <div className='flex flex-col items-center justify-center py-8'>
              <QrCode className='text-muted-foreground/40 mb-3 h-10 w-10' />
              <p className='text-muted-foreground text-sm'>
                {t('printLogEmpty')}
              </p>
            </div>
          ) : editing ? (
            <div className='space-y-4'>
              {/* Entity name card */}
              <div className='bg-muted rounded-lg p-3'>
                <p className='text-sm font-medium'>{entityName}</p>
              </div>

              {/* Edit inputs */}
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium'>
                    {t('firstPage')}
                  </label>
                  <div className='flex items-center gap-1'>
                    <Button
                      variant='outline'
                      size='icon'
                      className='h-8 w-8 shrink-0'
                      onClick={() =>
                        setEditStartPage(Math.max(1, editStartPage - 1))
                      }
                      disabled={editStartPage <= 1}
                    >
                      <Minus className='h-3 w-3' />
                    </Button>
                    <Input
                      type='number'
                      min={1}
                      value={editStartPage}
                      onChange={(e) =>
                        setEditStartPage(Math.max(1, Number(e.target.value)))
                      }
                      className='h-8 text-center'
                    />
                    <Button
                      variant='outline'
                      size='icon'
                      className='h-8 w-8 shrink-0'
                      onClick={() => setEditStartPage(editStartPage + 1)}
                    >
                      <Plus className='h-3 w-3' />
                    </Button>
                  </div>
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium'>
                    {t('lastPageCol')}
                  </label>
                  <div className='flex items-center gap-1'>
                    <Button
                      variant='outline'
                      size='icon'
                      className='h-8 w-8 shrink-0'
                      onClick={() =>
                        setEditEndPage(Math.max(editStartPage, editEndPage - 1))
                      }
                      disabled={editEndPage <= editStartPage}
                    >
                      <Minus className='h-3 w-3' />
                    </Button>
                    <Input
                      type='number'
                      min={editStartPage}
                      value={editEndPage}
                      onChange={(e) =>
                        setEditEndPage(
                          Math.max(editStartPage, Number(e.target.value))
                        )
                      }
                      className='h-8 text-center'
                    />
                    <Button
                      variant='outline'
                      size='icon'
                      className='h-8 w-8 shrink-0'
                      onClick={() => setEditEndPage(editEndPage + 1)}
                    >
                      <Plus className='h-3 w-3' />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Difference badge */}
              <div className='flex items-center justify-between'>
                <p className='text-muted-foreground text-sm'>
                  {t('totalSheets')}: {editTotalSheets}
                </p>
                {sheetDiff !== 0 && (
                  <Badge
                    variant='secondary'
                    className={cn(
                      sheetDiff > 0
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    )}
                  >
                    {sheetDiff > 0 ? '+' : ''}
                    {t('sheetDiff', { count: sheetDiff })}
                  </Badge>
                )}
              </div>

              {/* Action buttons */}
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  className='flex-1'
                  onClick={handleCancelEdit}
                >
                  {t('cancelEdit')}
                </Button>
                <Button
                  className='flex-1'
                  onClick={handleSaveEdit}
                  disabled={
                    updateMutation.isPending ||
                    editEndPage < editStartPage ||
                    editStartPage < 1
                  }
                >
                  <Check className='me-2 h-4 w-4' />
                  {t('saveChanges')}
                </Button>
              </div>
            </div>
          ) : (
            <div className='space-y-4'>
              {/* Entity name card */}
              <div className='bg-muted rounded-lg p-3'>
                <p className='text-sm font-medium'>{entityName}</p>
              </div>

              {/* Info grid */}
              <div className='grid grid-cols-2 gap-3'>
                {/* <div className='rounded-lg border p-3 text-center'>
                  <p className='text-muted-foreground text-xs'>
                    {t('totalContainerPages')}
                  </p>
                  <p className='mt-1 text-lg font-bold'>
                    {registration.expected_page_count ?? '—'}
                  </p>
                </div>
                <div className='rounded-lg border p-3 text-center'>
                  <p className='text-muted-foreground text-xs'>
                    {t('totalSheets')}
                  </p>
                  <p className='mt-1 text-lg font-bold'>{totalSheets}</p>
                </div> */}
                <div className='rounded-lg border p-3 text-center'>
                  <p className='text-muted-foreground text-xs'>
                    {t('firstPage')}
                  </p>
                  <p className='mt-1 text-lg font-bold'>{startPage}</p>
                </div>
                <div className='rounded-lg border p-3 text-center'>
                  <p className='text-muted-foreground text-xs'>
                    {t('lastPageCol')}
                  </p>
                  <p className='mt-1 text-lg font-bold'>{endPage}</p>
                </div>
              </div>

              {/* Saved / edited badge */}
              {(() => {
                const origStart = registration.latest_batch_original_start_page;
                const origEnd = registration.latest_batch_original_end_page;
                const hasBeenEdited =
                  origEnd != null &&
                  (startPage !== origStart || endPage !== origEnd);

                if (!hasBeenEdited) {
                  return (
                    <div className='flex justify-center'>
                      <Badge
                        variant='secondary'
                        className='bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      >
                        <Check className='me-1 h-3 w-3' />
                        {t('saved')}
                      </Badge>
                    </div>
                  );
                }

                const originalSheets = (origEnd ?? 0) - (origStart ?? 0) + 1;
                const viewDiff = totalSheets - originalSheets;

                return (
                  <div className='flex items-center justify-between rounded-lg border px-3 py-2'>
                    <span className='text-muted-foreground text-sm'>
                      {t('originalSheets', { count: originalSheets })}
                    </span>
                    <div className='flex items-center gap-2'>
                      <Badge
                        variant='secondary'
                        className='bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                      >
                        <Pencil className='me-1 h-2.5 w-2.5' />
                        {t('edited')}
                      </Badge>
                      {viewDiff !== 0 && (
                        <Badge
                          variant='secondary'
                          className={cn(
                            viewDiff > 0
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          )}
                        >
                          {viewDiff > 0 ? '+' : ''}
                          {t('sheetDiff', { count: viewDiff })}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Print buttons — both feed the same controlled confirmation
                  dialog. `printScope` decides whether the action prints the
                  whole current range or just the labels added by editing. */}
              <Button
                className='w-full'
                disabled={printing}
                onClick={() => {
                  setPrintScope('all');
                  setPrintConfirmOpen(true);
                }}
              >
                <Printer className='me-2 h-4 w-4' />
                {printing ? t('printing') : t('printQrCodes')}
              </Button>

              {extraPages.length > 0 && (
                <Button
                  variant='outline'
                  className='w-full'
                  disabled={printing}
                  onClick={() => {
                    setPrintScope('extra');
                    setPrintConfirmOpen(true);
                  }}
                >
                  <Printer className='me-2 h-4 w-4' />
                  {t('printExtraPagesCount', { count: extraPages.length })}
                </Button>
              )}

              <AlertDialog
                open={printConfirmOpen}
                onOpenChange={(open) => {
                  setPrintConfirmOpen(open);
                  // Reset scope so the always-mounted PrintPortal reverts to
                  // the full range after extras-only printing.
                  if (!open) setPrintScope('all');
                }}
              >
                <AlertDialogContent className='sm:max-w-2xl'>
                  <AlertDialogHeader>
                    <AlertDialogTitle className='flex items-center gap-2'>
                      {t('confirmPrintTitle')}
                      {printScope === 'extra' && (
                        <Badge
                          variant='secondary'
                          className='bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        >
                          {t('confirmPrintExtrasBadge')}
                        </Badge>
                      )}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {gridConfig.mode === 'grid' ||
                      gridConfig.mode === 'continuous'
                        ? t('confirmPrintGridDescription', {
                            count: labelInputs.length,
                            sheets: sheetCount,
                            perSheet: cellsPerSheet,
                            container: registration.container_number
                          })
                        : t('confirmPrintDescription', {
                            count: labelInputs.length,
                            container: registration.container_number
                          })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  {labelInputs.length > 0 && (
                    <>
                      {/* Summary bar */}
                      <div className='bg-muted/40 text-muted-foreground flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs'>
                        <div className='flex items-center gap-1.5'>
                          <span className='font-mono font-semibold tabular-nums'>
                            {labelInputs.length}
                          </span>
                          <span>{t('previewLabelsCount')}</span>
                          <span className='text-muted-foreground/60'>·</span>
                          <span className='font-mono font-semibold tabular-nums'>
                            {sheetCount}
                          </span>
                          <span>{t('previewSheetsNeeded')}</span>
                          {(gridConfig.mode === 'grid' ||
                            gridConfig.mode === 'continuous') && (
                            <>
                              <span className='text-muted-foreground/60'>
                                ·
                              </span>
                              <span>
                                {t('previewLabelsPerSheet', {
                                  count: cellsPerSheet
                                })}
                              </span>
                            </>
                          )}
                        </div>
                        <div className='font-mono'>
                          {gridConfig.mode === 'grid'
                            ? `${gridConfig.paperWidthMm} × ${gridConfig.paperHeightMm} mm @ ${labelDpi} dpi`
                            : gridConfig.mode === 'continuous'
                              ? `${continuousLayout.paperWidthMm.toFixed(2)} × ${continuousLayout.paperHeightMm.toFixed(2)} mm @ ${labelDpi} dpi`
                              : `${labelWidthMm} × ${labelHeightMm} mm @ ${labelDpi} dpi`}
                        </div>
                      </div>

                      {/* Preview area: branches by print mode */}
                      {gridConfig.mode === 'grid' ||
                      gridConfig.mode === 'continuous' ? (
                        <div className='max-h-80 overflow-y-auto rounded-md border bg-slate-100 p-3 dark:bg-slate-900'>
                          <div className='space-y-3'>
                            {sheets.map((sheetLabels, sheetIdx) => {
                              const previewW =
                                gridConfig.mode === 'continuous'
                                  ? continuousLayout.paperWidthMm
                                  : gridConfig.paperWidthMm;
                              const previewH =
                                gridConfig.mode === 'continuous'
                                  ? continuousLayout.paperHeightMm
                                  : gridConfig.paperHeightMm;
                              const previewCols =
                                gridConfig.mode === 'continuous'
                                  ? continuousLayout.cols
                                  : gridConfig.cols;
                              const previewRows =
                                gridConfig.mode === 'continuous'
                                  ? continuousLayout.rows
                                  : gridConfig.rows;
                              const previewMargin =
                                gridConfig.mode === 'continuous'
                                  ? continuousLayout.marginMm
                                  : gridConfig.marginMm;
                              return (
                                <div key={sheetIdx} className='space-y-1'>
                                  <div
                                    className='rounded-sm bg-white shadow-md ring-1 ring-slate-300 dark:bg-white dark:ring-slate-400'
                                    style={{
                                      aspectRatio: `${previewW} / ${previewH}`,
                                      padding: `${(previewMargin / Math.max(previewW, previewH)) * 100}%`,
                                      display: 'grid',
                                      gridTemplateColumns: `repeat(${previewCols}, 1fr)`,
                                      gridTemplateRows: `repeat(${previewRows}, 1fr)`,
                                      gap: `${(gapMm / Math.max(previewW, previewH)) * 100}%`
                                    }}
                                  >
                                    {Array.from({ length: cellsPerSheet }).map(
                                      (_, cellIdx) => {
                                        const label = sheetLabels[cellIdx];
                                        if (!label) {
                                          return (
                                            <div
                                              key={`empty-${cellIdx}`}
                                              className='rounded-[1px] border border-dashed border-slate-200 dark:border-slate-300'
                                            />
                                          );
                                        }
                                        return (
                                          <div
                                            key={`cell-${cellIdx}`}
                                            className='flex flex-col items-center justify-center gap-[2%] p-[4%]'
                                          >
                                            <span className='text-[6px] leading-none font-bold text-slate-900'>
                                              {label.headerText}
                                            </span>
                                            <QRCode
                                              value={label.qrPayload}
                                              level='M'
                                              style={{
                                                width: '90%',
                                                height: 'auto',
                                                maxHeight: '100%'
                                              }}
                                            />
                                            <span className='text-[6px] leading-none font-bold text-slate-900'>
                                              {label.footerText}
                                            </span>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                  <p className='text-muted-foreground text-center text-[10px]'>
                                    {t('previewSheetIndex', {
                                      current: sheetIdx + 1,
                                      total: sheetCount
                                    })}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className='max-h-80 overflow-y-auto rounded-md border bg-slate-100 p-3 dark:bg-slate-900'>
                          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
                            {labelInputs.map((label, idx) => (
                              <div key={`label-${idx}`} className='space-y-1'>
                                <div
                                  className='flex flex-col items-center justify-center gap-[3%] rounded-sm bg-white p-[6%] shadow-md ring-1 ring-slate-300 dark:bg-white dark:ring-slate-400'
                                  style={{
                                    aspectRatio: `${labelWidthMm} / ${labelHeightMm}`
                                  }}
                                >
                                  <span className='text-[8px] leading-none font-bold text-slate-900'>
                                    {label.headerText}
                                  </span>
                                  <QRCode
                                    value={label.qrPayload}
                                    level='M'
                                    style={{
                                      width: '85%',
                                      height: 'auto',
                                      maxHeight: '100%'
                                    }}
                                  />
                                  <span className='text-[8px] leading-none font-bold text-slate-900'>
                                    {label.footerText}
                                  </span>
                                </div>
                                <p className='text-muted-foreground text-center text-[10px]'>
                                  {t('previewSheetIndex', {
                                    current: idx + 1,
                                    total: labelInputs.length
                                  })}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('cancelEdit')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handlePrint}
                      disabled={printing}
                    >
                      <Printer className='me-2 h-4 w-4' />
                      {t('printQrCodes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Dev-only: Labelary preview — visible when NEXT_PUBLIC_ZEBRA_MOCK=1
                  AND running in development. Never calls Labelary in production. */}
              {ZEBRA_MOCK_ENABLED &&
                LABELARY_PREVIEW_ENABLED &&
                labelInputs.length > 0 && (
                  <div className='space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/30 dark:bg-amber-950/20'>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='text-xs font-medium text-amber-800 dark:text-amber-200'>
                        Preview (mock) — {labelInputs.length} label
                        {labelInputs.length > 1 ? 's' : ''} · {labelWidthMm}×
                        {labelHeightMm} mm @ {labelDpi} dpi
                      </div>
                      <div className='flex items-center gap-1.5'>
                        <Button
                          type='button'
                          size='sm'
                          variant='ghost'
                          onClick={() => void handleCopyZpl()}
                          title='Copy raw ZPL to clipboard'
                        >
                          <Copy className='me-1 h-3.5 w-3.5' />
                          Copy ZPL
                        </Button>
                        <a
                          href='https://labelary.com/viewer.html'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-amber-700 hover:underline dark:text-amber-300'
                        >
                          <Button type='button' size='sm' variant='ghost'>
                            <ExternalLink className='me-1 h-3.5 w-3.5' />
                            Open Labelary
                          </Button>
                        </a>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline'
                          onClick={() => void handlePreview()}
                          disabled={previewing}
                        >
                          {previewing ? (
                            <Loader2 className='me-2 h-4 w-4 animate-spin' />
                          ) : (
                            <Eye className='me-2 h-4 w-4' />
                          )}
                          {previewItems
                            ? 'Refresh preview'
                            : 'Preview via Labelary'}
                        </Button>
                      </div>
                    </div>
                    {previewItems && previewItems.length > 0 && (
                      <div className='max-h-96 overflow-y-auto rounded border border-amber-300 bg-white p-2 dark:border-amber-700 dark:bg-slate-900'>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
                          {previewItems.map((item) => (
                            <figure
                              key={item.index}
                              className='flex flex-col items-center gap-1 rounded border border-slate-200 bg-white p-1.5 dark:border-slate-700 dark:bg-slate-800'
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={item.url}
                                alt={`Label ${item.index + 1} preview`}
                                className='max-w-full'
                              />
                              <figcaption className='text-[10px] font-medium text-slate-500 dark:text-slate-400'>
                                #{item.index + 1}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* Complete indexing */}
              {registration.current_stage === 'sticking' && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant='outline' className='w-full'>
                      <ArrowRight className='me-2 h-4 w-4' />
                      {t('completeIndexing')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('completeIndexingTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('completeIndexingDescription')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('cancelEdit')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCompleteIndexing}
                        disabled={advanceStageMutation.isPending}
                      >
                        {t('confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print-only area — emergency fallback for when BrowserPrint is down.
          Sticker mode: `size: auto` defers to driver paper, one QR per page.
          Grid / continuous mode: `size: {paper}mm`, N×M cells per page,
          `page-break-after` after each sheet.
          Gated on `open` — this dialog stays mounted while closed (so it can
          reopen instantly), but `PrintPortal` renders into a page-wide shared
          `#__print-portal` node. Rendering the page-QR content unconditionally
          would leave it sitting in that shared node and get pulled into ANY
          unrelated `window.print()` call elsewhere on the page (e.g. the
          container header's "Reprint Sticker" fallback). */}
      {open && (
        <PrintPortal>
          {gridConfig.mode === 'grid' || gridConfig.mode === 'continuous' ? (
            (() => {
              const printW = cssNumber(
                gridConfig.mode === 'continuous'
                  ? continuousLayout.paperWidthMm
                  : gridConfig.paperWidthMm,
                0
              );
              const printH = cssNumber(
                gridConfig.mode === 'continuous'
                  ? continuousLayout.paperHeightMm
                  : gridConfig.paperHeightMm,
                0
              );
              const printCols = cssNumber(
                gridConfig.mode === 'continuous'
                  ? continuousLayout.cols
                  : gridConfig.cols,
                1
              );
              const printRows = cssNumber(
                gridConfig.mode === 'continuous'
                  ? continuousLayout.rows
                  : gridConfig.rows,
                1
              );
              const printMargin = cssNumber(
                gridConfig.mode === 'continuous'
                  ? continuousLayout.marginMm
                  : gridConfig.marginMm,
                0
              );
              const printGap = cssNumber(gapMm, 0);
              return (
                <>
                  <style
                    dangerouslySetInnerHTML={{
                      __html: `
@media print {
  /* Declare the page exactly the way the operator configured it. The
     printer driver's stock must also be set to this size or larger;
     otherwise Chrome will paginate. */
  @page { size: ${printW}mm ${printH}mm; margin: 0; }
  html, body {
    margin: 0;
    padding: 0;
    width: ${printW}mm;
    height: ${printH}mm;
    overflow: hidden;
    background: white;
  }
  /* Each sheet fills the @page exactly. 100vw/100vh tracks whatever the
     print engine decides the page is, which avoids overflow if the driver
     trims the page (e.g. unprintable margins). overflow:hidden + a tiny
     safety margin clip any sub-pixel overflow that would otherwise push
     content onto a new page. */
  .qr-sticker-sheet {
    width: 100vw;
    height: 100vh;
    max-width: ${printW}mm;
    max-height: ${printH}mm;
    padding: ${printMargin}mm;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: repeat(${printCols}, 1fr);
    grid-template-rows: repeat(${printRows}, 1fr);
    gap: ${printGap}mm;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
    background: white;
  }
  .qr-sticker-sheet:last-child { page-break-after: auto; break-after: auto; }
  /* Flex column with justify-center clusters header → QR → footer. Each
     side gets its own margin (qr_header_gap_mm / qr_footer_gap_mm) so
     the operator can tune the two distances independently. Spare
     vertical space (when the square QR is bounded by cell width on a
     tall/narrow cell) lives at the top/bottom edges of the cell instead
     of inflating the gap to the text. */
  .qr-sticker-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .qr-sticker-cell.empty { background: transparent; }
  .qr-sticker-cell > .qr-sticker-text {
    margin: 0;
    font-weight: 700;
    text-align: center;
    line-height: 1;
    font-size: clamp(5pt, 2.5vh, 9pt);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    flex-shrink: 0;
    color: #000;
  }
  .qr-sticker-cell > .qr-sticker-header {
    margin-bottom: ${styleHeaderGapMm}mm;
    ${styleHeaderFontMm > 0 ? `font-size: ${styleHeaderFontMm}mm;` : ''}
  }
  .qr-sticker-cell > .qr-sticker-footer {
    margin-top: ${styleFooterGapMm}mm;
    ${styleFooterFontMm > 0 ? `font-size: ${styleFooterFontMm}mm;` : ''}
  }
  .qr-sticker-cell > svg {
    flex: 0 1 auto;
    min-height: 0;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 1 / 1;
    display: block;
  }
}`
                    }}
                  />
                  <div className='hidden' data-print-zone>
                    {sheets.map((sheetLabels, sheetIdx) => (
                      <div key={sheetIdx} className='qr-sticker-sheet'>
                        {Array.from({ length: cellsPerSheet }).map(
                          (_, cellIdx) => {
                            const label = sheetLabels[cellIdx];
                            if (!label) {
                              return (
                                <div
                                  key={`empty-${cellIdx}`}
                                  className='qr-sticker-cell empty'
                                />
                              );
                            }
                            return (
                              <div
                                key={`cell-${cellIdx}`}
                                className='qr-sticker-cell'
                              >
                                <p className='qr-sticker-text qr-sticker-header'>
                                  {label.headerText}
                                </p>
                                <QRCode
                                  value={label.qrPayload}
                                  size={256}
                                  level='M'
                                />
                                <p className='qr-sticker-text qr-sticker-footer'>
                                  {label.footerText}
                                </p>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()
          ) : (
            <>
              <style
                dangerouslySetInnerHTML={{
                  __html: `
@media print {
  @page { size: auto; margin: 0; }
  html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: white; }
  /* See .qr-sticker-cell — same clustering strategy for single-sticker mode. */
  .qr-sticker-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    padding: 1.5mm;
    box-sizing: border-box;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
    background: white;
  }
  .qr-sticker-page:last-child { page-break-after: auto; break-after: auto; }
  .qr-sticker-page > .qr-sticker-text {
    margin: 0;
    font-weight: 700;
    text-align: center;
    line-height: 1;
    font-size: clamp(7pt, 4vh, 14pt);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    flex-shrink: 0;
    color: #000;
  }
  .qr-sticker-page > .qr-sticker-header {
    margin-bottom: ${styleHeaderGapMm}mm;
    ${styleHeaderFontMm > 0 ? `font-size: ${styleHeaderFontMm}mm;` : ''}
  }
  .qr-sticker-page > .qr-sticker-footer {
    margin-top: ${styleFooterGapMm}mm;
    ${styleFooterFontMm > 0 ? `font-size: ${styleFooterFontMm}mm;` : ''}
  }
  .qr-sticker-page > svg {
    flex: 0 1 auto;
    min-height: 0;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 1 / 1;
    display: block;
  }
}`
                }}
              />
              <div className='hidden' data-print-zone>
                {labelInputs.map((label, idx) => (
                  <div key={`label-${idx}`} className='qr-sticker-page'>
                    <p className='qr-sticker-text qr-sticker-header'>
                      {label.headerText}
                    </p>
                    {/* size is arbitrary — CSS scales the SVG to fill the grid cell. */}
                    <QRCode value={label.qrPayload} size={512} level='M' />
                    <p className='qr-sticker-text qr-sticker-footer'>
                      {label.footerText}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </PrintPortal>
      )}
    </>
  );
}
